import React, { useState, useRef, useEffect, useContext } from "react";
import { AppStateContext } from "../../state/AppProvider";
import { Spinner, ISpinnerStyles } from "@fluentui/react";
import { ButtonTooltip } from "../ButtonTooltip";
import { InfoTooltip } from "../InfoTooltip";

interface Props {
    onSend: (question: string) => void;
    disabled: boolean;
    lastQuestionRef: React.MutableRefObject<string>;
    placeholder?: string;
    clearOnSend?: boolean;
    fileInputRef?: React.RefObject<HTMLInputElement>;
    uploadFile?: () => void;
    showToEnd?: boolean;
    toEnd?: () => void;
}

export const QuestionInput = ({ onSend, disabled, lastQuestionRef, placeholder, clearOnSend, fileInputRef, uploadFile, showToEnd, toEnd }: Props) => {
    const appStateContext = useContext(AppStateContext);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    const calculateRows = (): void => {
        const textArea = textAreaRef.current;
        if (textArea) {
            textArea.style.height = "auto";
            const scrollHeight = textArea.scrollHeight;
            textArea.style.height = `${scrollHeight}px`;
        }
    };

    // Recalculate rows whenever the text changes
    useEffect(() => {
        calculateRows();
    }, [textAreaRef?.current?.value]);

    const [question, setQuestion] = useState<string>("");

    const sendQuestion = () => {
        if (disabled || !question.trim()) {
            return;
        }

        onSend(question);

        if (clearOnSend) {
            setQuestion("");
        }
    };

    const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
        if (ev.key === "Enter" && !ev.shiftKey) {
            ev.preventDefault();
            sendQuestion();
        }
    };

    const onQuestionChange = (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
        calculateRows();
        if (!ev.target.value) {
            setQuestion("");
        } else {
            appStateContext?.dispatch({ type: "SET_SUMMARISE_EXAMPLE_NO_DOCUMENT", payload: false });
            setQuestion(ev.target.value);
        }
    };

    const sendQuestionDisabled = disabled || !question.trim();

    const onUploadFileClick = () => {
        if (fileInputRef) {
            fileInputRef.current?.click();
        }
    };

    const spinnerStyles: ISpinnerStyles = {
        circle: {
            borderColor: "#0062b8 #0062b8 #bee1ff00 #bee1ff00",
            height: "16px",
            width: "16px"
        }
    };

    const removeFile = (index: number) => {
        appStateContext?.dispatch({ type: "SET_SUMMARISE_EXAMPLE_NO_DOCUMENT", payload: false });
        appStateContext?.dispatch({ type: "REMOVE_FILE", payload: index });
        if (fileInputRef && fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    return (
        <div className={"transition-[padding] duration-200 text-base m-auto px-4 " + (appStateContext?.state.isChatHistoryOpen ? "" : "lg:px-16")}>
            {showToEnd && (
                <div
                    className={"absolute left-0 right-0 z-[4] mx-auto w-10 animate-bounce"}
                    style={{ bottom: textAreaRef.current ? textAreaRef.current.scrollHeight + 40 : 92 }}
                >
                    <div className="flex cursor-pointer justify-center" onClick={toEnd}>
                        <svg width="32" height="32" viewBox="0 0 40 40" fill="#FFFFFF" xmlns="http://www.w3.org/2000/svg" className="">
                            <rect x="1" y="1" width="38" height="38" rx="19" stroke="#5B6E7F" stroke-width="2"></rect>
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M20 22.7092L26.532 16.5L28 17.8954L20 25.5L12 17.8954L13.468 16.5L20 22.7092Z"
                                fill="#5B6E7F"
                            ></path>
                        </svg>
                    </div>
                </div>
            )}
            <div className="mx-auto flex flex-1 text-base gap-4 lg:gap-6 lg:max-w-3xl">
                <div className="flex w-full items-center">
                    {/* {true && (
                        <div
                            className={"absolute left-0 right-0 z-[4] ml-0 w-56 animate-bounce"}
                            style={{ bottom: textAreaRef.current ? textAreaRef.current.scrollHeight + 40 : 92 }}
                        >
                            <div className="bg-sstl-light-grey text-sstl-dark border-b-[3px] border-solid border-sstl-orange text-sm py-2 px-3 shadow-[0_1px_3px_rgba(0,0,0,0.4)]  text-center">
                                Attach a file to get started
                            </div>
                        </div>
                    )} */}
                    <div className="overflow-hidden flex flex-col w-full flex-grow relative rounded-lg bg-sstl-white shadow-[0px_8px_16px_rgba(0,0,0,0.14),0px_0px_2px_rgba(0,0,0,0.12)]">
                        {appStateContext?.state.assistant?.attachments &&
                            appStateContext?.state.files &&
                            appStateContext?.state.files.length > 0 &&
                            appStateContext?.state.files
                                .filter(fileItem => fileItem.new)
                                .map((fileItem, index) => (
                                    <a key={index} className="gap-2 w-fit flex pl-3.5 pt-3.5 pb-1 items-center">
                                        {appStateContext?.state.files[index].status === "Loading" && (
                                            <span className="flex justify-center items-center h-[24px] w-[24px]">
                                                <Spinner style={{ display: "block", verticalAlign: "middle" }} styles={spinnerStyles} />
                                            </span>
                                        )}
                                        {appStateContext?.state.files[index].status === "Error" ? (
                                            <span className="text-sstl-yellow">{"Unable to upload " + fileItem.file.name}</span>
                                        ) : appStateContext?.state.files[index].status === "Locked" ? (
                                            <span className="text-sstl-yellow">
                                                {"Unable to upload " + fileItem.file.name + ". Is the file password protected?"}
                                            </span>
                                        ) : (
                                            <span className="text-sstl-dark">{fileItem.file.name}</span>
                                        )}
                                        <ButtonTooltip title="Remove file" placement="top">
                                            <button className="flex justify-center font-semibold text-sstl-blue" onClick={() => removeFile(index)}>
                                                <span className="material-symbols-outlined text-[1.25rem]">cancel</span>
                                            </button>
                                        </ButtonTooltip>
                                    </a>
                                ))}
                        <textarea
                            ref={textAreaRef}
                            className={
                                "m-0 w-full resize-none border-0 bg-transparent focus-visible:outline-0 focus:ring-0 focus-visible:ring-0 py-3.5 pr-12 placeholder-sstl-dark max-h-[200px] min-h-[52px]" +
                                (appStateContext?.state.assistant?.attachments ? " pl-12" : " pl-4")
                            }
                            placeholder={placeholder}
                            rows={1}
                            value={question}
                            onChange={onQuestionChange}
                            onKeyDown={onEnterPress}
                        ></textarea>
                        {appStateContext?.state.assistant?.attachments && (
                            <>
                                <input
                                    className="hidden"
                                    type="file"
                                    accept=".pdf,.doc,.docx,.txt"
                                    ref={fileInputRef}
                                    onChange={uploadFile}
                                    multiple
                                    maxLength={2}
                                />
                                <div className="absolute bottom-3 left-3 h-[28px]">
                                    {disabled ? (
                                        <a className={"text-[1.75rem] material-symbols-outlined text-sstl-dark cursor-default"}>attach_file</a>
                                    ) : appStateContext?.state.summariseExampleNoDocument &&
                                        appStateContext?.state.files?.length === 0 &&
                                        !lastQuestionRef.current ? (
                                        <InfoTooltip title="Attach a file to summarise" placement="top" disableHoverListener={true} open={true}>
                                            <a className={"text-[1.75rem] material-symbols-outlined cursor-pointer text-sstl-blue"} onClick={onUploadFileClick}>
                                                attach_file
                                            </a>
                                        </InfoTooltip>
                                    ) : (
                                        <ButtonTooltip title="Attach up to 2 files" placement="top">
                                            <a className={"text-[1.75rem] material-symbols-outlined cursor-pointer text-sstl-blue"} onClick={onUploadFileClick}>
                                                attach_file
                                            </a>
                                        </ButtonTooltip>
                                    )}
                                </div>
                            </>
                        )}
                        <div className={"absolute bottom-3 right-3 h-[28px] rounded-lg" + (sendQuestionDisabled ? " bg-sstl-white" : " bg-sstl-yellow")}>
                            <a
                                className={
                                    "text-[1.75rem] material-symbols-outlined text-sstl-white" + (sendQuestionDisabled ? " cursor-default" : " cursor-pointer")
                                }
                                onClick={sendQuestion}
                            >
                                arrow_upward_alt
                            </a>
                        </div>
                    </div>
                </div>
            </div>
            <div className="relative px-2 py-2 text-center text-xs text-sstl-dark">
                <span className="lg:hidden inline-block">Content provided by SIMBA is AI-generated.</span>
                <span className="hidden lg:inline-block">Content provided by SIMBA is AI-generated. Always check important information.</span>
            </div>
        </div>
    );
};
